import {
  faCloudArrowUp,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { transparentize } from 'polished';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Loader } from 'src/components/Loader';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';

interface AlertImageFieldProps {
  label: string;
  value: string;
  error?: string;
  helperText?: string;
  required?: boolean;
  uploading?: boolean;
  aspectRatio?: string;
  height?: number | string;
  onSelect: (file: File) => void;
  onRemove: () => void;
}

export const AlertImageField = ({
  label,
  value,
  error,
  helperText,
  required = false,
  uploading = false,
  aspectRatio = '4 / 3',
  height,
  onSelect,
  onRemove,
}: AlertImageFieldProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'admin.platform.appAlertsForm.imageField',
  });
  const { getInputProps, getRootProps, isDragActive } = useDropzone({
    accept: { 'image/*': [] },
    multiple: false,
    disabled: uploading,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles[0]) {
        onSelect(acceptedFiles[0]);
      }
    },
  });

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2">
        {label}
        {required ? ' *' : ''}
      </Typography>
      <Box
        {...getRootProps()}
        sx={{
          position: 'relative',
          width: '100%',
          ...(height ? { height } : { aspectRatio }),
          borderRadius: 2,
          overflow: 'hidden',
          border: (theme) =>
            `1px dashed ${
              error
                ? theme.palette.error.main
                : isDragActive
                  ? theme.palette.primary.main
                  : theme.palette.divider
            }`,
          backgroundColor: isDragActive
            ? 'action.hover'
            : value
              ? 'background.default'
              : 'background.paper',
          cursor: uploading ? 'not-allowed' : 'pointer',
          transition: 'border-color 0.2s ease, background-color 0.2s ease',
        }}
      >
        <input {...getInputProps()} />
        {value ? (
          <Box
            component="img"
            src={value}
            alt=""
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : null}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
            backgroundColor:
              isDragActive || !value
                ? 'transparent'
                : (theme) => transparentize(0.88, theme.palette.common.black),
            transition: 'background-color 0.2s ease',
          }}
        >
          {!value ? (
            <Stack spacing={1.5} sx={{ alignItems: 'center', textAlign: 'center' }}>
              <Icon icon={faCloudArrowUp} size="large" />
              <Typography variant="body2">
                {t('selectPrompt', {
                  defaultValue: 'Click to upload or drag and drop',
                })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('empty', {
                  defaultValue: 'No image selected',
                })}
              </Typography>
            </Stack>
          ) : isDragActive ? (
            <Typography
              variant="body2"
              sx={{
                px: 1.5,
                py: 0.75,
                borderRadius: 999,
                backgroundColor: (theme) =>
                  transparentize(0.08, theme.palette.background.paper),
              }}
            >
              {t('replace', {
                defaultValue: 'Drop image to replace',
              })}
            </Typography>
          ) : null}
        </Box>
        {value ? (
          <IconButton
            size="small"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onRemove();
            }}
            disabled={uploading}
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              zIndex: 2,
              backgroundColor: (theme) =>
                transparentize(0.08, theme.palette.background.paper),
              border: (theme) =>
                `1px solid ${transparentize(0.92, theme.palette.common.black)}`,
              '&:hover': {
                backgroundColor: 'background.paper',
              },
            }}
          >
            <Icon icon={faTrash} size="small" />
          </IconButton>
        ) : null}
        {uploading ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: (theme) =>
                transparentize(0.28, theme.palette.background.paper),
            }}
          >
            <Loader size="small" sx={{ minWidth: 'auto', width: 'auto' }} />
          </Box>
        ) : null}
      </Box>
      {!!helperText && (
        <Typography variant="caption" color="text.secondary">
          {helperText}
        </Typography>
      )}
      {!!error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
    </Stack>
  );
};
